/**
 * useEntityUISchema Hook (DRR-045)
 *
 * Fetches context-aware UI schemas from the backend form_schema_api.
 * Used by DynamicFormGenerator to render forms based on:
 * - Entity type (JournalEntry, GlAccount, Patient)
 * - UI context (Mode × Role × SafetyLevel)
 *
 * This hook implements the "dynamic" part of the hybrid approach.
 */

import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useMemo } from 'react';
import type { WidgetType } from '../widgets/types';

/**
 * Supported UI schema format version.
 * Must match CURRENT_UI_SCHEMA_VERSION in graphql_gateway/src/ui_schema/types.rs.
 * Frontend checks major version match; minor differences are forward-compatible.
 */
export const SUPPORTED_SCHEMA_VERSION = '1.0';

// =============================================================================
// TYPES
// =============================================================================

/** UI modes for context resolution */
export type UIMode = 'CREATE' | 'EDIT' | 'VIEW' | 'APPROVE' | 'FILTER';

/** Safety levels */
export type SafetyLevel = 'STANDARD' | 'ELEVATED' | 'CRITICAL' | 'LIFE_CRITICAL';

/** Context for schema resolution */
export interface UIContext {
  mode: UIMode;
  role?: string;
  safetyLevel?: SafetyLevel;
  tenantId: string;
}

/** Field definition from schema */
export interface UISchemaField {
  name: string;
  path: string;
  fieldType: WidgetType | string;
  label: string;
  required: boolean;
  placeholder?: string;
  helpText?: string;
  defaultValue?: unknown;
  options?: Array<{ value: string; label: string }>;
  visibleWhen?: {
    field: string;
    operator: string;
    value?: string;
  };
  disabledWhen?: {
    field: string;
    operator: string;
    value?: string;
  };
  validation?: {
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    min?: number;
    max?: number;
  };
  piiClassification?: string;
  auditRequired?: boolean;
  width?: string;
  order?: number;
  componentProps?: Record<string, unknown>;
  // DRR-060: Type-specific configuration for complex field types
  computedConfig?: {
    expression: string;
    computationTier: string;
    displayFormat?: string;
    dependencies?: string[];
  };
  hierarchicalConfig?: {
    dataSource: string;
    valuePath?: string;
    labelPath?: string;
    parentField?: string;
    maxDepth?: number;
    selectionMode: string;
    searchable?: boolean;
    lazyLoad?: boolean;
  };
  gridConfig?: {
    columns: Array<{
      fieldName: string;
      fieldType: string;
      label: string;
      width?: string;
      editable?: boolean;
      options?: Array<{ value: string; label: string }>;
    }>;
    minRows?: number;
    maxRows?: number;
    allowAddRow?: boolean;
    allowRemoveRow?: boolean;
    showRowNumbers?: boolean;
    aggregations?: Array<{
      column: string;
      function: string;
      label?: string;
    }>;
  };
  masterDetailConfig?: {
    masterField: string;
    detailOptionsQuery: string;
    filterExpression?: string;
    cascadeClear?: boolean;
  };
  /** Reference config for REFERENCE field type (cross-subgraph entity lookup) */
  referenceConfig?: {
    subgraph: string;
    entityType: string;
    query?: string;
    valuePath?: string;
    labelPath?: string;
    searchFields?: string[];
    allowCreate?: boolean;
  };
  /** Custom renderer key (for CUSTOM field types) */
  rendererKey?: string;
  /** JSON-encoded props for custom renderer */
  customRendererProps?: string;
}

/** Section in the form layout */
export interface UISchemaSection {
  id: string;
  title: string;
  description?: string;
  fields: string[];
  columns?: number;
  collapsible?: boolean;
  collapsed?: boolean;
  safetyWarning?: string;
  order?: number;
}

/** Complete UI schema for an entity */
export interface EntityUISchema {
  subgraph: string;
  entityType: string;
  version: string;
  /** UI schema format version (e.g., "1.0"). Used for compatibility checks. */
  schemaVersion: string;
  displayName: string;
  description?: string;
  safetyLevel: SafetyLevel;
  fields: UISchemaField[];
  layout: {
    layoutType: 'single-page' | 'stepper' | 'tabs' | 'accordion';
    sections: UISchemaSection[];
    submitLabel?: string;
    cancelLabel?: string;
    showProgress?: boolean;
  };
  validations?: Array<{
    id: string;
    expression: string;
    message: string;
    fields: string[];
    severity?: 'error' | 'warning' | 'info';
  }>;
  createMutation?: string;
  updateMutation?: string;
  fetchQuery?: string;
}

/** Hook return type */
export interface UseEntityUISchemaResult {
  schema: EntityUISchema | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

// =============================================================================
// GRAPHQL
// =============================================================================

const GET_ENTITY_UI_SCHEMA = gql`
  query GetEntityUISchema(
    $subgraph: String!
    $entityType: String!
    $rulesetId: String
  ) {
    formSchema(
      subgraph: $subgraph
      entityType: $entityType
      rulesetId: $rulesetId
    ) {
      subgraph
      entityType
      version
      displayName
      description
      safetyLevel
      fields {
        name
        path
        fieldType
        label
        required
        placeholder
        helpText
        defaultValue
        options {
          value
          label
          group
          disabled
          description
        }
        visibleWhen {
          field
          operator
          value
        }
        disabledWhen {
          field
          operator
          value
        }
        minLength
        maxLength
        pattern
        patternMessage
        min
        max
        piiClassification
        auditRequired
        maskOnDisplay
        complianceFrameworks
        width
        order
        computedConfig {
          expression
          computationTier
          displayFormat
          dependencies
        }
        hierarchicalConfig {
          dataSource
          valuePath
          labelPath
          parentField
          maxDepth
          selectionMode
          searchable
          lazyLoad
        }
        gridConfig {
          columns {
            fieldName
            fieldType
            label
            width
            editable
            options {
              value
              label
            }
          }
          minRows
          maxRows
          allowAddRow
          allowRemoveRow
          showRowNumbers
          aggregations {
            column
            function
            label
          }
        }
        masterDetailConfig {
          masterField
          detailOptionsQuery
          filterExpression
          cascadeClear
        }
      }
      layout {
        layoutType
        sections {
          id
          title
          description
          fields
          columns
          collapsible
          collapsed
          safetyWarning
          order
        }
        submitLabel
        cancelLabel
        showProgress
      }
      validations {
        id
        expression
        message
        fields
        severity
      }
      createMutation
      updateMutation
      fetchQuery
    }
  }
`;

// =============================================================================
// HOOK
// =============================================================================

/**
 * Fetch context-aware UI schema for an entity type
 *
 * @example
 * ```tsx
 * const { schema, loading, error } = useEntityUISchema({
 *   subgraph: 'accounting',
 *   entityType: 'JournalEntry',
 *   context: {
 *     mode: 'CREATE',
 *     role: 'accountant',
 *     safetyLevel: 'STANDARD',
 *     tenantId: 'tenant-123',
 *   },
 * });
 * ```
 */
/** Parse defaultValue: try JSON first, fall back to raw string.
 *  Backend stores defaultValue as a String (e.g., "active", "true", "0").
 *  Plain strings like "active" are not valid JSON, so JSON.parse would throw. */
function safeParseDefaultValue(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

export function useEntityUISchema({
  subgraph,
  entityType,
  rulesetId,
  context: _context,
  skip = false,
}: {
  subgraph: string;
  entityType: string;
  /** DRR-162: Optional RuleSet ID for service-line-specific form override resolution.
   * Pass from constellation instance's primary_ruleset for multi-service-line tenants. */
  rulesetId?: string;
  /** @deprecated Backend resolves context from auth token. Kept for caller compat. */
  context?: UIContext;
  skip?: boolean;
}): UseEntityUISchemaResult {
  const { data, loading, error, refetch } = useQuery<Record<string, any>>(GET_ENTITY_UI_SCHEMA, {
    variables: {
      subgraph,
      entityType,
      rulesetId,
    },
    skip,
    fetchPolicy: 'cache-and-network', // Use cache but also check for updates
  });

  const schema = useMemo((): EntityUISchema | null => {
    if (!data?.formSchema) return null;

    const raw = data?.formSchema;

    return {
      subgraph: raw.subgraph,
      entityType: raw.entityType,
      version: raw.version,
      schemaVersion: SUPPORTED_SCHEMA_VERSION,
      displayName: raw.displayName,
      description: raw.description,
      safetyLevel: raw.safetyLevel as SafetyLevel,
      fields: raw.fields.map((f: any) => ({
        name: f.name,
        path: f.path,
        fieldType: f.fieldType,
        label: f.label,
        required: f.required,
        placeholder: f.placeholder,
        helpText: f.helpText,
        defaultValue: f.defaultValue ? safeParseDefaultValue(f.defaultValue) : undefined,
        options: f.options,
        visibleWhen: f.visibleWhen,
        disabledWhen: f.disabledWhen,
        validation: {
          minLength: f.minLength,
          maxLength: f.maxLength,
          pattern: f.pattern,
          min: f.min,
          max: f.max,
        },
        piiClassification: f.piiClassification,
        auditRequired: f.auditRequired,
        width: f.width,
        order: f.order,
        // DRR-060: Type-specific configs for complex field types
        computedConfig: f.computedConfig ?? undefined,
        hierarchicalConfig: f.hierarchicalConfig ?? undefined,
        gridConfig: f.gridConfig ?? undefined,
        masterDetailConfig: f.masterDetailConfig ?? undefined,
      })),
      layout: {
        layoutType: raw.layout.layoutType,
        sections: raw.layout.sections,
        submitLabel: raw.layout.submitLabel,
        cancelLabel: raw.layout.cancelLabel,
        showProgress: raw.layout.showProgress,
      },
      validations: raw.validations,
      createMutation: raw.createMutation,
      updateMutation: raw.updateMutation,
      fetchQuery: raw.fetchQuery,
    };
  }, [data]);

  return {
    schema,
    loading,
    error: error || null,
    refetch,
  };
}

/**
 * Helper to get ordered fields from schema
 */
export function getOrderedFields(schema: EntityUISchema): UISchemaField[] {
  return [...schema.fields].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
}

/**
 * Helper to get fields for a specific section
 */
export function getSectionFields(
  schema: EntityUISchema,
  sectionId: string
): UISchemaField[] {
  const section = schema.layout.sections.find((s) => s.id === sectionId);
  if (!section) return [];

  return section.fields
    .map((fieldName) => schema.fields.find((f) => f.name === fieldName))
    .filter((f): f is UISchemaField => f !== undefined);
}

export default useEntityUISchema;
